import { Inject, Injectable, OnDestroy } from '@angular/core';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus, PopupRequest, RedirectRequest } from '@azure/msal-browser';
import { BehaviorSubject, filter, firstValueFrom, Observable, Subject, take, takeUntil } from 'rxjs';
import { LOGIN_TYPE, LoginType, LOGOUT_TYPE, LogoutType } from '../entra-id-config';
import { LOGIN_BEHAVIOR } from '../providers/login-behavior.provider';
import { LOGOUT_BEHAVIOR } from '../providers/logout-behavior.provider';

@Injectable({ providedIn: 'root' })
export class EntraIdService implements OnDestroy {
  private static readonly LS_TOKEN_KEY = 'token';
  private static readonly LS_TOKEN_EXPIRATION = 'expires_at';

  public loginProgress$ = new BehaviorSubject<boolean>(true);

  private readonly _destroying$ = new Subject<void>();
  private _isLoggedIn = new BehaviorSubject<boolean>(false);
  private _loginSuccess = new BehaviorSubject<boolean>(false);
  private isMsalInitialized = false;

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private readonly msalGuardConfig: MsalGuardConfiguration,
    @Inject(LOGIN_BEHAVIOR) private readonly loginType: LoginType,
    @Inject(LOGOUT_BEHAVIOR) private readonly logoutType: LogoutType,
    private readonly authService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
  ) {}

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  async init(): Promise<void> {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$),
      )
      .subscribe({
        next: () => {
          this.loginProgress$.next(false);
          this.checkLoggedIn();
        },
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status !== InteractionStatus.None),
        takeUntil(this._destroying$),
      )
      .subscribe({
        next: () => {
          this.loginProgress$.next(true);
        },
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS),
        takeUntil(this._destroying$),
      )
      .subscribe({
        next: (msg) => {
          this.addTokenToLocalstorage(msg);
          this._isLoggedIn.next(true);
          this._loginSuccess.next(true);
        },
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE || msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
        takeUntil(this._destroying$),
      )
      .subscribe({
        next: () => {
          this._isLoggedIn.next(false);
          this._loginSuccess.next(false);
          this.removeTokenFromLocalstorage();
        },
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGOUT_SUCCESS),
        takeUntil(this._destroying$),
      )
      .subscribe({
        next: () => {
          this._isLoggedIn.next(false);
          this.removeTokenFromLocalstorage();
        },
      });

    await this.initMsal();
  }

  // Log the user in and redirect them if MSAL provides a redirect URI otherwise go to the default URI
  async login() {
    if (!this.isMsalInitialized) {
      await this.initMsal();
    }
    if (this.msalGuardConfig.authRequest) {
      if (this.loginType === LOGIN_TYPE.POPUP) {
        this.authService.loginPopup({
          ...this.msalGuardConfig.authRequest,
        } as PopupRequest);
      } else {
        this.authService.loginRedirect({
          ...this.msalGuardConfig.authRequest,
        } as RedirectRequest);
      }
    } else {
      if (this.loginType === LOGIN_TYPE.POPUP) {
        this.authService.loginPopup();
      } else {
        this.authService.loginRedirect();
      }
    }
  }

  // Log the user out
  async logout() {
    if (!this.isMsalInitialized) {
      await this.initMsal();
    }
    if (this.logoutType === LOGOUT_TYPE.POPUP) {
      this.authService.logoutPopup();
    } else if (this.logoutType === LOGOUT_TYPE.LOGOUT) {
      this.authService.logout();
    } else {
      this.authService.logoutRedirect();
    }
  }

  async silentLogout() {
    await this.authService.instance.clearCache();
    this._isLoggedIn.next(false);
  }

  isLoggedIn(): Observable<boolean> {
    return this._isLoggedIn.asObservable();
  }

  loginSuccess(): Observable<boolean> {
    return this._loginSuccess.asObservable();
  }

  getLogin(): boolean {
    return this.checkLoggedIn();
  }

  getIdToken(): string | null {
    return localStorage.getItem(EntraIdService.LS_TOKEN_KEY);
  }

  getExpiration(): string | null {
    return localStorage.getItem(EntraIdService.LS_TOKEN_EXPIRATION);
  }

  // If the user is logged in, present the user with a "logged in" experience
  private checkLoggedIn() {
    const loggedIn = this.authService.instance.getAllAccounts().length > 0;
    this._isLoggedIn.next(loggedIn);
    return loggedIn;
  }

  private async initMsal() {
    try {
      this.msalBroadcastService.msalSubject$
        .pipe(
          filter((msg: EventMessage) => msg.eventType === EventType.INITIALIZE_END),
          take(1),
        )
        .subscribe(() => {
          this.checkLoggedIn();
          this.isMsalInitialized = true;
        });
      await firstValueFrom(this.authService.initialize());
      await firstValueFrom(this.authService.handleRedirectObservable());
    } catch (error) {
      console.error('MSAL initialization error:', error);
    }
  }

  private addTokenToLocalstorage(msg: EventMessage) {
    const token = (msg.payload as any).idToken;
    localStorage.setItem(EntraIdService.LS_TOKEN_KEY, token);
    localStorage.setItem('user_token', token); // Needs to be temporary saved into user_token as well. Otherwise the servicepoint will have a hard cut and it will lead to bad user experience
    const tokenExpiration = (msg.payload as any).expiresOn;
    localStorage.setItem(EntraIdService.LS_TOKEN_EXPIRATION, tokenExpiration);
  }

  private removeTokenFromLocalstorage(): void {
    localStorage.removeItem(EntraIdService.LS_TOKEN_KEY);
    localStorage.removeItem('user_token');
    localStorage.removeItem(EntraIdService.LS_TOKEN_EXPIRATION);
  }
}
